import { EventEmitter, Injectable } from '@angular/core';
import { EcgState } from '../view-models/ecgstate';
import { ECG_CONSTANTS } from '../utils/ecg-constants';
import { ArrangementType, PaperRate, TimeAlignment } from '../models/enums';
import { ArrangementComposer } from './arrangementComposer';
import { CabreraComposer } from './cabreraComposer';
import { TimeAlignmentComposer } from './timeAlignmentComposer';
import { PaperRateComposer } from './paperRateComposer';
import { TraceSpacingComposer } from './traceSpacingComposer';
import { ChartSpacingComposer } from './chartSpacingComposer';
import { IChartContext, ILayerComposer, ILayerViewModel, ILayout } from '../models/interfaces';

@Injectable()
export class Layout implements ILayout {
    // events

    context: IChartContext | null = null;
    rows: number = ECG_CONSTANTS.TRACE_ROWS;

    // data members
    columns: number = ECG_CONSTANTS.TRACE_COLUMNS;
    private _dictionary: Record<string, ILayerComposer> = {};

    constructor() {
        this._dictionary.arrangement = new ArrangementComposer(this);
        this._dictionary.cabrera = new CabreraComposer(this);
        this._dictionary.timeAlignment = new TimeAlignmentComposer(this);
        this._dictionary.paperRate = new PaperRateComposer(this);
        this._dictionary.traceSpacing = new TraceSpacingComposer(this);
        this._dictionary.chartSpacing = new ChartSpacingComposer(this);
    }

    private _layoutChange = new EventEmitter<ILayerViewModel>();

    // properties

    get layoutChange() {
        return this._layoutChange;
    }

    private _ecgState: EcgState = new EcgState();

    get ecgState(): EcgState {
        return this._ecgState;
    }

    // constructor

    set ecgState(ecgState: EcgState) {
        this._ecgState = ecgState;
        this.update();
    }

    // ICalculationContext interface

    get leadWidth(): number {
        return this.context?.leadWidth ?? 0;
    }
    get leadHeight(): number {
        return this.context?.leadHeight ?? 0;
    }
    get period(): number {
        return this.ecgState.chartState.arrangement == ArrangementType.Medians ? 1 : this.columns;
    }

    // public functions

    init(context: IChartContext) {
        this.context = context;
        this.update();
    }

    updateSingle(layer: ILayerViewModel, force?: boolean) {
        this.updateMillimeterPaper();
        this.updateLeadWidth();
        Object.values(this._dictionary).forEach((layoutComposer) => layoutComposer.updateSingle(layer, force));
    }

    update(force?: boolean) {
        this.updateMillimeterPaper();
        this.updateLeadWidth();
        Object.values(this._dictionary).forEach((layoutComposer) =>
            layoutComposer.update(this.context?.layers ?? [], force),
        );
        this.context?.layers.forEach((layer) => this.updateTraceTitles(layer));
    }

    getPosition(index: number) {
        const leadHeight = this.context?.leadHeight ?? 0;
        const leadWidth = this.context?.leadWidth ?? 0;
        if (index === this.rows * this.columns) {
            let positionX = ECG_CONSTANTS.LEAD_SPACE_WIDTH;
            let positionY = this.rows * leadHeight;
            return { x: positionX, y: positionY };
        }

        let column = Math.floor(index / this.rows);
        let row = index % this.rows;

        let positionX = ECG_CONSTANTS.LEAD_SPACE_WIDTH + column * (leadWidth + ECG_CONSTANTS.LEAD_SPACE_WIDTH);
        let positionY = row * leadHeight;

        return { x: positionX, y: positionY };
    }

    getSize() {
        const leadHeight = this.context?.leadHeight ?? 0;
        const leadWidth = this.context?.leadWidth ?? 0;
        return {
            width: ECG_CONSTANTS.LEAD_SPACE_WIDTH + this.columns * (leadWidth + ECG_CONSTANTS.LEAD_SPACE_WIDTH),
            height: this.rows * leadHeight,
            //height: (this.ecgState.chartState.arrangement == ArrangementType.ThreeXFour ? this.rows + 1 : this.rows) * this.context.leadHeight
        };
    }

    getLayerPosition(index: number) {
        const leadHeight = this.context?.leadHeight ?? 0;
        const layersLength = this.context?.layers?.length ?? 0;
        let row = index % layersLength;

        let positionX = 0;
        let positionY = row * leadHeight;

        return { x: positionX, y: positionY };
    }

    // private functions

    updateTraceTitles(layer: ILayerViewModel) {
        if (this.ecgState.chartState.arrangement == ArrangementType.Medians || layer !== this.context?.activeLayer) {
            layer.traces.forEach((trace) => {
                if (!!trace.title) trace.title.visible = false;
            });

            return;
        }

        layer.referenceTraces.forEach((trace) => {
            if (!!trace.title) trace.title.visible = false;
        });

        const visibleTraces = layer.traces.filter((t) => t.visible);

        for (let column = 0; column < this.columns; column++) {
            for (let row = 0; row < this.rows + 1; row++) {
                //   var index = column * this.rows + row;
                //   var trace = layer.traces.find(item => item.index == index);
                let trace = visibleTraces[column * this.rows + row];
                if (!trace) {
                    continue;
                }

                let seconds = this.safeDivide(trace.channel?.samplingCount, trace.channel?.samplingFrequency);

                switch (this.ecgState.chartState.timeAlignment) {
                    case TimeAlignment.Sequential:
                        if (!!trace.title)
                            trace.title.text =
                                ((column * seconds) / this.columns).toFixed(2) +
                                ' - ' +
                                (((column + 1) * seconds) / this.columns).toFixed(2) +
                                ' sec';
                        break;
                    default:
                        if (!!trace.title)
                            trace.title.text = (0).toFixed(2) + ' - ' + (seconds / this.columns).toFixed(2) + ' sec';
                        break;
                }

                if (!!trace.title) trace.title.visible = row == 0 ? true : false;
                if (!!trace.title && !trace.title.visible) trace.title.text = '';
            }
        }
    }

    updateMillimeterPaper() {
        if (!this.context) return;
        this.context.opacity = this.ecgState.chartState.paperOpacity;
    }

    updateLeadWidth() {
        let widthBasedOnPaperRate = this.calculateLeadWidth(
            this.ecgState.chartState.paperRate == PaperRate.mm25
                ? ECG_CONSTANTS.LEAD_WIDTH_25mm
                : ECG_CONSTANTS.LEAD_WIDTH_50mm,
        );

        widthBasedOnPaperRate =
            this.ecgState.chartState.arrangement == ArrangementType.Medians
                ? widthBasedOnPaperRate
                : Math.floor(widthBasedOnPaperRate / this.columns);
        const rate = this.safeDivide(
            this.context?.layers?.[0]?.traces?.[0]?.channelSelector?.derivedChannel?.samples.length,
            this.context?.layers?.[0]?.traces?.[0]?.channelSelector?.originalChannel?.samples.length,
        );
        let newLeadWidth =
            this.ecgState.chartState.arrangement == ArrangementType.Medians
                ? Math.floor(widthBasedOnPaperRate * rate)
                : widthBasedOnPaperRate;

        this.context?.updateLeadWidth(newLeadWidth);
    }

    private safeDivide = (num?: number | null, den?: number | null): number => {
        if (num === undefined || num === null || !den) return 1;
        return num / den;
    };

    private calculateLeadWidth(referenceLeadWidth: number) {
        if (
            this.context?.activeLayer === null ||
            this.context?.activeLayer.traces.length === 0 ||
            this.ecgState.chartState.arrangement == ArrangementType.Medians
        )
            return referenceLeadWidth;

        let seconds = this.safeDivide(
            this.context?.activeLayer?.traces?.[0]?.channel?.samplingCount,
            this.context?.activeLayer?.traces?.[0]?.channel?.samplingFrequency,
        );

        let leadWidth = (referenceLeadWidth * seconds) / 10;

        return leadWidth;
    }
}
